<template>
    <div class="tu">
        <!-- Tagged users display -->
        <div class="tu__list" v-if="taggedUsers.length">
            <div class="tu__chip" v-for="user in taggedUsers" :key="user._id || user.id">
                <span class="tu__avatar">{{ getInitials(user) }}</span>
                <span class="tu__name">{{ user.firstName }}{{ user.lastName ? ' ' + user.lastName : '' }}</span>
                <button
                    v-if="!viewMode"
                    class="tu__remove"
                    @click="removeUser(user)"
                    title="Remove"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>
        <div v-else-if="viewMode" class="tu__empty">No tagged users</div>

        <!-- Tag UI (edit mode) -->
        <div v-if="!viewMode" class="tu__actions">
            <button
                class="tu__add-btn"
                @click="showDropdown = !showDropdown"
                :disabled="processing"
            >
                <i :class="showDropdown ? 'fas fa-chevron-up' : 'fas fa-user-plus'"></i>
                {{ showDropdown ? 'Close' : 'Tag User' }}
            </button>
            <button
                v-if="showDropdown && !hideSave && hasChanges"
                class="tu__save-btn"
                @click="updateTagging"
                :disabled="processing"
            >
                <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                {{ processing ? 'Saving...' : 'Save' }}
            </button>
        </div>

        <!-- Dropdown -->
        <transition name="tu-slide">
            <div v-if="!viewMode && showDropdown" class="tu__dropdown">
                <div class="tu__search-wrap">
                    <i class="fas fa-search tu__search-icon"></i>
                    <input
                        ref="searchInput"
                        class="tu__search"
                        type="text"
                        v-model="search"
                        placeholder="Search users..."
                    />
                </div>
                <div class="tu__options">
                    <div
                        v-for="user in filteredUsers"
                        :key="user._id || user.id"
                        :class="['tu__option', { 'tu__option--selected': isSelected(user) }]"
                        @click="toggleUser(user)"
                    >
                        <span class="tu__opt-avatar" :class="{ 'tu__opt-avatar--sel': isSelected(user) }">
                            {{ getInitials(user) }}
                        </span>
                        <span class="tu__opt-name">{{ user.firstName }}{{ user.lastName ? ' ' + user.lastName : '' }}</span>
                        <i v-if="isSelected(user)" class="fas fa-check tu__opt-check"></i>
                    </div>
                    <div v-if="!filteredUsers.length" class="tu__no-results">
                        <i class="fas fa-user-slash"></i> No users found
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapState } from "vuex"
import { http } from "@/utils/http"

export default {
    emits: ["change", "update"],
    computed: {
        ...mapState("project", ["allAssignees"]),
        filteredUsers() {
            if (!this.allAssignees) return []
            if (!this.search) return this.allAssignees
            const q = this.search.toLowerCase()
            return this.allAssignees.filter(u => {
                const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase()
                return name.includes(q)
            })
        },
        hasChanges() {
            const original = (this.taggedUsers || []).map(u => String(u._id || u.id)).sort().join(',')
            const current = (this.selectedUsers || []).map(String).sort().join(',')
            return original !== current
        },
    },
    props: {
        viewMode: { type: Boolean, default: false },
        hideSave: { type: Boolean, default: false },
        taggedUsers: { type: Array, default: () => [] },
        updateRoute: { type: String, default: "" },
        createRoute: { type: String, default: "" },
    },
    data() {
        return {
            processing: false,
            selectedUsers: [],
            showDropdown: false,
            search: "",
        }
    },
    watch: {
        taggedUsers: {
            handler(val) {
                this.selectedUsers = (val || []).map(item => item._id || item.id)
            },
            immediate: true,
        },
        showDropdown(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) this.$refs.searchInput.focus()
                })
            }
        },
    },
    methods: {
        getInitials(user) {
            const f = (user.firstName || '')[0] || ''
            const l = (user.lastName || '')[0] || ''
            return (f + l).toUpperCase() || '?'
        },
        isSelected(user) {
            const id = String(user._id || user.id)
            return this.selectedUsers.map(String).includes(id)
        },
        toggleUser(user) {
            const id = user._id || user.id
            if (this.isSelected(user)) {
                this.selectedUsers = this.selectedUsers.filter(u => String(u) !== String(id))
            } else {
                this.selectedUsers = [...this.selectedUsers, id]
            }
            this.$emit("change", this.selectedUsers)
        },
        removeUser(user) {
            const id = String(user._id || user.id)
            this.selectedUsers = this.selectedUsers.filter(u => String(u) !== id)
            this.$emit("change", this.selectedUsers)
            // If has updateRoute, auto-save removal
            if (this.updateRoute && !this.hideSave) {
                this.updateTagging()
            }
        },
        async updateTagging() {
            if (this.processing) return
            this.processing = true
            try {
                const formData = new FormData()
                formData.append("taggedUsers", JSON.stringify(this.selectedUsers))
                if (this.updateRoute) {
                    const res = await http.patch(this.updateRoute, formData)
                    this.$emit("update", res.data.result)
                } else if (this.createRoute) {
                    const res = await http.post(this.createRoute, formData)
                    this.$emit("update", res.data.result)
                }
                this.showDropdown = false
                this.search = ""
            } catch (err) {
                if (this.$toast) this.$toast.error("Could not update!")
            }
            this.processing = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.tu { position: relative; }

/* ── Tagged user chips ── */
.tu__list { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.tu__chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 10px 4px 4px; border-radius: 20px;
    background: $gray50; border: 1px solid $gray200;
    font-size: 12px; font-weight: 500; color: $gray700;
    transition: all $transitionFast;
    &:hover { border-color: $gray300; background: white; }
}
.tu__avatar {
    width: 24px; height: 24px; border-radius: 50%;
    background: $primaryColor; color: white;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; flex-shrink: 0;
}
.tu__name { white-space: nowrap; }
.tu__remove {
    width: 18px; height: 18px; border-radius: 50%;
    border: none; background: transparent; color: $gray400;
    font-size: 9px; cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all $transitionFast; padding: 0; margin-left: 2px;
    &:hover { background: rgba($danger, .1); color: $danger; }
}
.tu__empty { font-size: 12px; color: $gray400; font-style: italic; padding: 4px 0; }

/* ── Action buttons ── */
.tu__actions { display: flex; align-items: center; gap: 6px; }
.tu__add-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 7px; font-size: 12px; font-weight: 600;
    border: 1px solid $gray200; background: white; color: $gray600;
    cursor: pointer; font-family: inherit; transition: all $transitionFast;
    &:hover { background: $gray50; border-color: $gray300; }
    &:disabled { opacity: .5; cursor: not-allowed; }
    i { font-size: 11px; }
}
.tu__save-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 14px; border-radius: 7px; font-size: 12px; font-weight: 600;
    border: none; background: $primaryColor; color: white;
    cursor: pointer; font-family: inherit; transition: all $transitionFast;
    &:hover { background: $primaryDark; }
    &:disabled { opacity: .6; cursor: not-allowed; }
    i { font-size: 10px; }
}

/* ── Dropdown ── */
.tu__dropdown {
    position: absolute; left: 0; right: 0; z-index: 1050;
    margin-top: 4px; background: white;
    border: 1px solid $gray200; border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
    overflow: hidden; max-width: 360px;
}
.tu__search-wrap {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 12px; border-bottom: 1px solid $gray100;
}
.tu__search-icon { font-size: 12px; color: $gray400; }
.tu__search {
    flex: 1; border: none; outline: none;
    font-size: 13px; font-family: inherit; color: $gray700; background: transparent;
    &::placeholder { color: $gray400; }
}
.tu__options {
    max-height: 220px; overflow-y: auto; padding: 4px;
    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: $gray300; border-radius: 3px; }
}
.tu__option {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 6px; cursor: pointer;
    transition: background $transitionFast;
    &:hover { background: $gray50; }
    &--selected { background: $primaryLight; }
}
.tu__opt-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    background: $gray200; color: $gray600;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; flex-shrink: 0;
    transition: all $transitionFast;
    &--sel { background: $primaryColor; color: white; }
}
.tu__opt-name { flex: 1; font-size: 13px; font-weight: 500; color: $gray700; }
.tu__opt-check { font-size: 11px; color: $primaryColor; }
.tu__no-results {
    padding: 20px 12px; text-align: center;
    font-size: 13px; color: $gray400;
    i { margin-right: 4px; }
}

/* ── Transition ── */
.tu-slide-enter-active { transition: opacity .15s, transform .15s; }
.tu-slide-leave-active { transition: opacity .1s, transform .1s; }
.tu-slide-enter, .tu-slide-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
